import * as Sentry from '@sentry/browser';
import { Severity } from '@sentry/browser';
import { AxiosError } from 'axios';
import { getUserId } from './storageUtils';

let sentryInitialized = false;

// Type guard to check if error is an AxiosError
const isAxiosError = (error: any): error is AxiosError => {
  return error?.isAxiosError === true || (error?.response && error?.config);
};

export const initSentry = () => {
  if (!sentryInitialized && process.env.REACT_APP_SENTRY) {
    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY,
      environment: process.env.REACT_APP_ENV || 'development',
      release: process.env.REACT_APP_VERSION || '1.0.0',
    });
    sentryInitialized = true;
  }
};

export const captureException = (
  error: Error | AxiosError | any,
  options?: { isErrorBoundary?: boolean }
) => {
  if (!sentryInitialized) {
    initSentry();
  }
  if (isAxiosError(error) && error.response?.status === 401) {
    if (process.env.REACT_APP_CONSOLE_LOGS_ENABLED === 'true') {
      console.log('401 error (not sent to Sentry):', error);
    }
    return;
  }

  // Determine release tag from environment variable or default based on env
  const releaseTag =
    process.env.REACT_APP_SENTRY_RELEASE ||
    (process.env.REACT_APP_ENV === 'production' ? 'main' : 'canary');

  // Check if it's an axios error and add additional context
  if (isAxiosError(error)) {
    // This is an axios error
    const axiosContext = {
      status: error.response?.status || 'Unknown Status',
      statusText: error.response?.statusText || 'Unknown Status Text',
      url: error.config?.url || 'Unknown URL',
      method: error.config?.method || 'Unknown Method',
      data: error.response?.data || 'No response data',
      errorCode: error.code || 'Unknown Error Code',
      errorMessage: error.message || 'Unknown Error Message',
    };

    Sentry.withScope((scope) => {
      scope.setContext('axios_error', axiosContext);
      scope.setTag('error_type', 'axios');
      scope.setTag('release', releaseTag);
      scope.setTag('http_status', error.response?.status || 'unknown');
      scope.setTag('userId', getUserId());
      scope.setLevel(Severity.Critical);
      if (options?.isErrorBoundary) {
        scope.setTag('error_source', 'error_boundary');
      }
      Sentry.captureException(error);
    });
  } else {
    // Regular error
    Sentry.withScope((scope) => {
      scope.setTag('release', releaseTag);
      scope.setTag('userId', getUserId());
      if (options?.isErrorBoundary) {
        scope.setTag('error_source', 'error_boundary');
      }
      Sentry.captureException(error);
    });
  }

  // Also log to console in development
  if (process.env.REACT_APP_CONSOLE_LOGS_ENABLED === 'true') {
    console.log('Error captured:', error);
    if (isAxiosError(error)) {
      console.log('Axios error details:', {
        status: error.response?.status,
        url: error.config?.url,
        data: error.response?.data,
      });
    }
  }
};
